import {Component, inject, OnInit} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef
} from "@angular/material/dialog";
import {TranslatePipe} from "@ngx-translate/core";
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatFormField, MatLabel, MatPrefix, MatSuffix} from '@angular/material/form-field';
import {MatOption} from '@angular/material/select';
import {AsyncPipe} from '@angular/common';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatAutocomplete, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  last,
  map,
  Observable,
  shareReplay,
  startWith,
  switchMap
} from 'rxjs';
import {FormCollectionHal, FormHal} from '../../models/HAL/form-hal.model';
import {CaseHal} from '../../models/HAL/case-hal.model';
import {HttpClient} from '@angular/common/http';
import {DocumentDtoModel} from '../../models/DTO/DocumentDto.model';
import {DocumentHelperService} from '../../services/document.helper.service';
import {MatProgressSpinner} from '@angular/material/progress-spinner';

@Component({
  selector: 'app-add-forms-dialog',
  imports: [
    MatDialogContent,
    TranslatePipe,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    MatFormField,
    MatOption,
    FormsModule,
    AsyncPipe,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatIcon,
    MatInput,
    MatLabel,
    MatPrefix,
    MatSuffix,
    ReactiveFormsModule,
    MatIconButton,
    MatProgressSpinner
  ],
  templateUrl: './add-forms-dialog.component.html',
  styleUrl: './add-forms-dialog.component.scss'
})
export class AddFormsDialogComponent implements OnInit {

  protected searchFormsFormControl = new FormControl('');
  protected filteredForms: Observable<FormHal[]> = new Observable<FormHal[]>();
  protected chosenForms: FormHal[] = [];
  protected data = inject(MAT_DIALOG_DATA);
  protected case: CaseHal = new CaseHal(this.data.caseHal);
  protected formType: string = this.data.formType;
  protected highestPosition: number = this.data.highestPosition;
  protected uploading: boolean = false;
  protected loadingSearchResult: boolean = false;

  constructor(private readonly httpClient: HttpClient,
              private readonly documentHelperService: DocumentHelperService,
              private readonly dialogRef: MatDialogRef<AddFormsDialogComponent>) {
    this.loadingSearchResult = true;
  }

  ngOnInit(): void {
    this.searchFormsFormControl.valueChanges.pipe(
      debounceTime(250)
    ).subscribe(() => {
      this.loadingSearchResult = true;
    });
    this.filteredForms = this.searchFormsFormControl.valueChanges.pipe(
      startWith(''),
      map((value) => value ?? ''),
      distinctUntilChanged(),
      debounceTime(250),
      switchMap((searchParameter) => {
        return this.executeFormSearch(searchParameter).pipe(
          map((response: { _embedded: { forms: FormHal[] } }) => {
            return response._embedded?.forms || [];
          })
        )
      }),
      shareReplay(1)
    );
    this.filteredForms.subscribe(() => {
      this.loadingSearchResult = false;
    });
  }

  protected selectForm(form: FormHal) {
    this.chosenForms.push(form);
    this.searchFormsFormControl.setValue('');
    this.loadingSearchResult = false;
  }

  protected executeFormSearch(searchParameter: string): Observable<any> {
    let url: URL = new URL(this.case._links.getLink("searchForms")!.href);
    // Remove HATEOAS searchParam placeholder if it exists
    url.searchParams.has("searchParameter") && url.searchParams.delete("searchParameter");
    url.searchParams.set("searchParameter", searchParameter);
    url.searchParams.set("formType", this.formType);
    return this.httpClient.get<FormCollectionHal>(url.toString());
  }

  protected removeForm(formToRemove: FormHal) {
    this.chosenForms = this.chosenForms.filter((form: FormHal) => form.identifier !== formToRemove.identifier);
  }

  protected addForms() {
    this.uploading = true;
    this.searchFormsFormControl.disable();
    this.loadingSearchResult = false;
    const documentsToAdd: DocumentDtoModel[] = [];
    this.chosenForms.forEach(form => {
      documentsToAdd.push(new DocumentDtoModel({
        title: form.title,
        formIdentifier: form.identifier,
        position: this.highestPosition + 1,
      }));
      this.highestPosition++;
    });
    forkJoin(
      documentsToAdd.map(document =>
        this.httpClient.post<CaseHal>(<string>this.case?._links.getLink('addForms')?.href, this.documentHelperService.getMultiPartForDocument(document))
      )
    ).pipe(
      last()
    ).subscribe({
      next: () => {
        this.dialogRef.close(true);
      }, error: () => {
        this.dialogRef.close(false);
      }
    });
  }
}
